import React from 'react';
import { Global } from '@emotion/core';

import { colors } from '../constants';
import { Button } from '../forms';
import { Heading, Text } from '../typography';
import { Modal, useOnClickOutside } from '../modal';
import { Close } from '../icons';

import { deliveryTimes } from '../../shared/delivery-times';

const QRCode = React.lazy(() => import('../qr-code'));

// Additional colors only used here.
const grey = '#c4c4c4';
const lightGrey = '#e0e0e0';
const lightLightGrey = '#f5f5f5';
const lightBlack = '#333';

const months = [
  'janúar',
  'febrúar',
  'mars',
  'apríl',
  'maí',
  'júní',
  'júlí',
  'ágúst',
  'september',
  'október',
  'nóvember',
  'desember',
];

function getFormattedDate(date, timeOfDay) {
  const dateNum = date.getDate();
  const monthName = months[date.getMonth()];
  return `${dateNum}. ${monthName}, ${timeOfDay}`;
}

function getDeliveryTime(date) {
  const hours = date.getHours();
  const day = date.getDay();
  const dateTomorrow = new Date(Date.now() + 60 * 60 * 24 * 1000);
  const [hour, deliveryTimeToday, deliveryTimeTomorrow] = deliveryTimes[day];
  return hours < hour
    ? [date, deliveryTimeToday]
    : [dateTomorrow, deliveryTimeTomorrow];
}

// Given a timestamp (in this module the order creation time), return the date
// and time time of day after which it can be picked up.
function getAvailableAt(timestamp) {
  const date = new Date(timestamp);
  const [deliveryDate, deliveryTime] = getDeliveryTime(date);
  const dateNum = deliveryDate.getDate();
  const monthName = months[deliveryDate.getMonth()];

  // Return the month, day and hour after which the order is available.
  return `${dateNum}. ${monthName}, ${deliveryTime}`;
}

export function MyOrder() {
  const [step, setStep] = React.useState(0);
  const [order, setOrder] = React.useState(null);
  const [modal, setModal] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  function fetchData() {
    const token = window.location.pathname.split('/')[2];
    fetch(`/my-order/${token}`)
      .then(res => res.json())
      .then(results => {
        if (results.error || !results.order) {
          // Avoid polling if there's a 404. Other errors may be temporary.
          if (results.error === 'Not found') {
            setNotFound(true);
          }

          setModal({
            title: 'Pöntun fannst ekki',
            body:
              'Pöntun fannst ekki í kerfinu. Vinsamlegast hafið samband við söluaðila.',
          });
          return;
        }

        setOrder({
          availableAt: getAvailableAt(results.order.createdAt),
          ...results.order,
        });

        if (results.order.status === 'consignment') {
          setStep(3);
        } else if (results.order.status === 'delivered') {
          setStep(4);
          setModal({
            title: 'Afhendingu lokið',
            body: 'Pöntunin þín hefur verið afhent.',
          });
        } else {
          setStep(2);
        }
      });
  }

  React.useEffect(() => {
    fetchData();
  }, []);

  useInterval(() => {
    // Only fetch when the browser/tab is active
    if (
      !document.hidden &&
      (!order || order.status !== 'delivered') &&
      !notFound
    ) {
      fetchData();
    }
  }, 2000);

  function closeModal() {
    window.location = 'https://dropp.is';
  }

  const innerContainer = React.createRef();
  useOnClickOutside(innerContainer, closeModal);

  const [height, setHeight] = React.useState(window.innerHeight);
  React.useEffect(() => {
    function onResize(event) {
      setHeight(window.innerHeight);
    }

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <>
      <Global
        styles={{
          'html, body': {
            fontSize: 16,
            fontFamily: 'GT Eesti Pro Text, "sans-serif"',
            fontWeight: 300,
            color: colors.blue,
            backgroundColor: colors.background,
            height: '100%',
            minHeight: '100vh',
          },
          html: {
            boxSizing: 'border-box',
          },
          '*, *:before, *:after': {
            boxSizing: 'inherit',
          },
          '#root': {
            height: '100%',
          },
          main: {
            position: 'initial !important',
            height: '100%',
            maxHeight: 'inherit !important',
            padding: '0 !important',
          },
          'main>div': {
            maxWidth: 'inherit !important',
          },
        }}
      />
      <Modal isOpen={!!modal}>
        <div
          ref={innerContainer}
          css={{
            position: 'relative',
            margin: 20,
            width: 360,
            padding: '16px 12px',
            backgroundColor: colors.white,
            borderRadius: 5,
            boxShadow: '0 2px 10px 2px rgba(0, 0, 0, .25)',
          }}
        >
          <Heading>{modal && modal.title}</Heading>
          <Close
            width={20}
            height={20}
            color={colors.blue}
            role="button"
            onClick={closeModal}
            styles={{ position: 'absolute', top: 16, right: 12 }}
          />
          <div css={{ marginTop: 24 }}>
            <Text>{modal && modal.body}</Text>
          </div>
          <Button onClick={closeModal} css={{ marginTop: 24 }}>
            Loka
          </Button>
        </div>
      </Modal>

      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: height ? height : '100%',
          width: '100%',
          backgroundColor: lightLightGrey,
        }}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: 62,
            backgroundColor: colors.white,
            padding: '0 21px',
            boxShadow: '0 2px 4px #d2d2d2',
          }}
        >
          <Heading css={{ fontSize: 20, fontWeight: 500 }}>
            {order ? `Pöntun frá ${order.storeName}` : 'Pöntun'}
          </Heading>
          <Logo />
        </div>
        <div
          css={{
            flex: 1,
            width: 300,
            margin: '0 auto',
            textAlign: 'center',
            overflow: 'auto',
          }}
        >
          {order && (
            <div css={{ marginTop: 32 }}>
              <Text>
                {step === 2
                  ? `Afhendist á ${order.locationName}`
                  : 'Sendingin er tilbúin til afhendingar á'}
              </Text>
              <Text css={{ marginTop: 6, fontSize: 20, fontWeight: 400 }}>
                {step === 2 ? order.availableAt : order.locationName}
              </Text>
            </div>
          )}
          <React.Suspense fallback={null}>
            <QRCode
              color={step === 2 ? colors.lightGrey : colors.black}
              code={order && order.qrCode}
              size={132}
              styles={{
                padding: 12,
                backgroundColor: colors.white,
                borderRadius: 3,
                boxShadow: `0 2px 4px ${grey}`,
                marginTop: 40,
                marginBottom: 32,
              }}
            />
          </React.Suspense>
          {order && (
            <Text css={{ color: lightBlack, opacity: 0.9 }}>
              {step === 2
                ? 'Þegar sendingin er tilbúin geturðu sótt hana með því að sýna QR kóðann'
                : 'Vinsamlegast sýndu starfsmanni N1 QR kóðann til að fá sendinguna.'}
            </Text>
          )}
        </div>
        <div
          css={{
            position: 'relative',
            backgroundColor: colors.white,
            height: 82,
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'center',
            alignItems: 'center',
            p: {
              flex: 1,
              padding: 12,
            },
          }}
        >
          <ProgressBar
            step={step}
            styles={{ position: 'absolute', top: 0, left: 0, right: 0 }}
          />
          <Text css={{ opacity: 0.7 }}>Pöntun móttekin</Text>
          <Text css={step === 2 ? { fontWeight: 400 } : { opacity: 0.7 }}>
            Í undirbúningi
          </Text>
          <Text css={step === 3 ? { fontWeight: 400 } : { opacity: 0.7 }}>
            Tilbúin til afhendingar
          </Text>
        </div>
      </div>
    </>
  );
}

export function useInterval(callback, delay) {
  const savedCallback = React.useRef();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

const Step = ({ isDone, styles = {} }) => (
  <div
    css={{
      borderRadius: '50%',
      backgroundColor: isDone ? colors.teal : lightGrey,
      width: 20,
      height: 20,
      border: `4px solid ${isDone ? colors.blue : grey}`,
      ...styles,
    }}
  />
);

const ProgressBar = ({ step, styles = {} }) => (
  <div
    css={{
      height: 6,
      width: '100%',
      backgroundColor: lightGrey,
      ...styles,
    }}
  >
    <div
      css={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: 6,
        width: '50%',
        width: step > 2 ? '100%' : '50%',
        backgroundColor: step > 0 ? colors.blue : lightGrey,
      }}
    />
    <Step
      isDone={step > 0}
      styles={{
        position: 'absolute',
        top: -6,
        left: 'calc(16% - 10px)',
      }}
    />
    <Step
      isDone={step > 1}
      styles={{
        position: 'absolute',
        top: -6,
        left: 'calc(50% - 10px)',
      }}
    />
    <Step
      isDone={step > 2}
      styles={{
        position: 'absolute',
        top: -6,
        left: 'calc(84% - 10px)',
      }}
    />
  </div>
);

const Logo = () => (
  <svg
    width="91"
    height="23"
    viewBox="0 0 91 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.77378 9.06958C8.37381 8.73809 7.88605 8.52934 7.36958 8.46864C6.8531 8.40793 6.33006 8.49785 5.86377 8.72752L9.93155 11.0618L8.77378 9.06958Z"
      fill="#00FFFF"
    />
    <path
      d="M15.3875 8.44978C14.7597 8.44922 14.1517 8.66944 13.6706 9.0717L12.5107 11.0618L16.5806 8.7172C16.2086 8.5383 15.8005 8.44682 15.3875 8.44978Z"
      fill="#00FFFF"
    />
    <path
      d="M11.2244 0.943359C9.19147 0.943359 7.20421 1.54459 5.51391 2.67102C3.8236 3.79745 2.50618 5.39849 1.72822 7.27167C0.950255 9.14485 0.746704 11.2061 1.1433 13.1946C1.53991 15.1832 2.51885 17.0098 3.95633 18.4435C5.39381 19.8771 7.22528 20.8535 9.21913 21.249C11.213 21.6446 13.2796 21.4416 15.1578 20.6657C17.036 19.8898 18.6413 18.5758 19.7707 16.89C20.9001 15.2042 21.5029 13.2222 21.5029 11.1947C21.5002 8.4767 20.4164 5.87083 18.4894 3.94893C16.5624 2.02703 13.9496 0.946103 11.2244 0.943359ZM17.9506 8.80236C17.9517 8.8127 17.9517 8.82311 17.9506 8.83345V8.86662V8.8915C17.9481 8.90215 17.9447 8.91256 17.9403 8.92259L17.9278 8.95162L17.9153 8.9765L17.9008 9.00345L17.8821 9.0304L17.8633 9.05527V9.06771L17.8509 9.07807C17.8445 9.0855 17.8375 9.09243 17.8301 9.09881C17.8235 9.10677 17.8158 9.11376 17.8072 9.11954L17.7823 9.14027L17.7553 9.15685H17.7428L12.2013 12.3473L14.1365 13.4626C14.3888 13.6075 14.5982 13.8163 14.7434 14.0679C14.8899 14.3199 14.9674 14.6059 14.9679 14.8972V17.2543C14.968 17.5454 14.8912 17.8315 14.7452 18.0837C14.5993 18.3359 14.3893 18.5453 14.1365 18.6909L12.0911 19.8663C11.8384 20.0119 11.5516 20.0885 11.2597 20.0885C10.9678 20.0885 10.6811 20.0119 10.4283 19.8663L8.38296 18.6909C8.13011 18.5453 7.92016 18.3359 7.77421 18.0837C7.62826 17.8315 7.55146 17.5454 7.55153 17.2543V14.9075C7.55146 14.6167 7.6274 14.3309 7.77185 14.0783C7.91826 13.8276 8.12739 13.619 8.3788 13.473L10.314 12.3597L4.77247 9.16722H4.76L4.73297 9.15063L4.70803 9.1299C4.69945 9.12412 4.69175 9.11714 4.68516 9.10917L4.6623 9.08844L4.65191 9.07601C4.65191 9.07601 4.65191 9.076 4.65191 9.06564L4.6332 9.04076L4.61449 9.01381L4.59994 8.98686L4.58747 8.96198L4.575 8.93296C4.57061 8.92293 4.56713 8.91252 4.56461 8.90186V8.87699V8.84382C4.5636 8.83348 4.5636 8.82306 4.56461 8.81272C4.56358 8.80308 4.56358 8.79335 4.56461 8.7837C4.56461 8.7837 4.56461 8.7837 4.56461 8.76711C4.54891 7.87664 4.71121 6.99196 5.04203 6.16471C5.37285 5.33747 5.86557 4.58423 6.49143 3.94896C7.11729 3.31369 7.86375 2.80911 8.68725 2.46467C9.51074 2.12023 10.3948 1.94284 11.2878 1.94284C12.1808 1.94284 13.0648 2.12023 13.8883 2.46467C14.7118 2.80911 15.4583 3.31369 16.0841 3.94896C16.71 4.58423 17.2027 5.33747 17.5335 6.16471C17.8643 6.99196 18.0266 7.87664 18.0109 8.76711C18.0109 8.76711 18.0109 8.77748 18.0109 8.7837C17.9926 8.79451 17.9719 8.8009 17.9506 8.80236Z"
      fill="#00FFFF"
    />
    <path
      d="M11.2222 8.44971C10.7019 8.44857 10.1925 8.59917 9.75684 8.88298L11.2222 11.4142L12.6876 8.88298C12.2519 8.59939 11.7426 8.44881 11.2222 8.44971Z"
      fill="#00FFFF"
    />
    <path
      d="M11.2243 0C9.00437 0 6.83426 0.656551 4.98843 1.88663C3.1426 3.1167 1.70395 4.86505 0.854405 6.91059C0.00486275 8.95613 -0.217415 11.207 0.215677 13.3785C0.64877 15.55 1.71778 17.5447 3.28753 19.1103C4.85728 20.6759 6.85727 21.7421 9.03457 22.174C11.2119 22.606 13.4687 22.3843 15.5197 21.537C17.5707 20.6897 19.3237 19.2549 20.557 17.4139C21.7903 15.573 22.4486 13.4086 22.4486 11.1946C22.4453 8.22659 21.2617 5.38112 19.1575 3.28245C17.0532 1.18378 14.2002 0.00329226 11.2243 0ZM11.2243 21.4459C9.19142 21.4459 7.20416 20.8446 5.51386 19.7182C3.82356 18.5918 2.50613 16.9908 1.72817 15.1176C0.950208 13.2444 0.746656 11.1832 1.14326 9.19463C1.53986 7.20607 2.5188 5.37946 3.95628 3.94579C5.39377 2.51212 7.22523 1.53577 9.21908 1.14022C11.2129 0.744672 13.2796 0.947684 15.1578 1.72358C17.0359 2.49948 18.6412 3.81342 19.7706 5.49924C20.9001 7.18506 21.5029 9.16705 21.5029 11.1946C21.5001 13.9125 20.4163 16.5184 18.4893 18.4403C16.5623 20.3622 13.9495 21.4431 11.2243 21.4459Z"
      fill="#00007E"
    />
    <path
      d="M12.7085 3.18408C12.8843 3.50722 13.0297 3.84588 13.1429 4.19574C13.5156 5.39377 13.7253 6.63626 13.7665 7.88994C14.2571 7.64399 14.7973 7.51228 15.3464 7.50474C15.8955 7.4972 16.4391 7.61402 16.9363 7.84641C16.7591 6.74477 16.2654 5.71802 15.515 4.89051C14.7646 4.06301 13.7898 3.47027 12.7085 3.18408Z"
      fill="white"
    />
    <path
      d="M13.9886 17.2647V15.0361L11.7021 16.3587V18.9998L13.6352 17.8866C13.7434 17.8226 13.8329 17.7316 13.8948 17.6225C13.9568 17.5134 13.9891 17.3901 13.9886 17.2647Z"
      fill="white"
    />
    <path
      d="M11.5816 13.1081C11.4725 13.0444 11.3484 13.0107 11.222 13.0107C11.0956 13.0107 10.9714 13.0444 10.8624 13.1081L8.92725 14.2214L11.2137 15.5419L13.5001 14.2214L11.567 13.1081H11.5816Z"
      fill="white"
    />
    <path
      d="M11.2201 2.99365C11.1584 2.99735 11.0989 3.01738 11.0476 3.0517C10.9454 3.12067 10.8555 3.20615 10.7815 3.30461C10.5216 3.66747 10.3242 4.07105 10.1974 4.4987C9.86198 5.59338 9.67326 6.72752 9.63623 7.87158C10.1317 7.63276 10.675 7.50871 11.2253 7.50871C11.7756 7.50871 12.3189 7.63276 12.8144 7.87158C12.787 7.27151 12.7252 6.6735 12.6294 6.08046C12.5262 5.38832 12.3442 4.71019 12.0869 4.05921C11.9742 3.77177 11.8201 3.50221 11.6296 3.259C11.5635 3.17877 11.4857 3.10889 11.3989 3.0517C11.3476 3.01738 11.288 2.99735 11.2263 2.99365H11.2201Z"
      fill="white"
    />
    <path
      d="M10.7483 18.9997V16.3586L8.46191 15.0381V17.2646C8.4622 17.3905 8.49555 17.5142 8.55864 17.6233C8.62172 17.7324 8.71235 17.8232 8.8215 17.8865L10.7483 18.9997Z"
      fill="white"
    />
    <path
      d="M7.05486 7.50862C7.62165 7.50688 8.1808 7.63901 8.68654 7.89422C8.7175 7.23497 8.7869 6.57808 8.8944 5.92688C9.00942 5.16196 9.21238 4.41279 9.49926 3.69418C9.57101 3.52025 9.6529 3.35066 9.74453 3.18628C8.66438 3.47413 7.69093 4.06778 6.94178 4.89552C6.19262 5.72325 5.69983 6.74965 5.52295 7.85068C6.00185 7.62481 6.52511 7.50797 7.05486 7.50862Z"
      fill="white"
    />
    <path
      d="M15.9762 4.01353C15.0358 3.07604 13.8379 2.43764 12.5338 2.17905C11.2297 1.92045 9.878 2.05325 8.64957 2.56068C7.42113 3.0681 6.3711 3.92735 5.63221 5.02984C4.89332 6.13232 4.49874 7.42853 4.49833 8.75463C4.49833 8.75463 4.49833 8.765 4.49833 8.77122C4.4973 8.78087 4.4973 8.79059 4.49833 8.80024C4.49732 8.81058 4.49732 8.82099 4.49833 8.83134V8.8645V8.88938C4.50085 8.90004 4.50433 8.91045 4.50872 8.92048L4.5212 8.9495L4.53367 8.97438L4.54822 9.00133L4.56692 9.02828L4.58564 9.05315C4.58564 9.05315 4.58564 9.05315 4.58564 9.06352L4.59603 9.07596L4.61889 9.09669C4.62547 9.10465 4.63318 9.11164 4.64175 9.11742L4.6667 9.13815L4.69372 9.15473H4.70619L10.2477 12.3473L8.31252 13.4605C8.06111 13.6065 7.85198 13.8151 7.70557 14.0658C7.56112 14.3184 7.48518 14.6042 7.48524 14.8951V17.2521C7.48518 17.5433 7.56198 17.8294 7.70793 18.0816C7.85388 18.3338 8.06383 18.5432 8.31668 18.6888L10.362 19.8642C10.6148 20.0098 10.9015 20.0864 11.1934 20.0864C11.4853 20.0864 11.7721 20.0098 12.0249 19.8642L14.0702 18.6888C14.323 18.5432 14.533 18.3338 14.6789 18.0816C14.8249 17.8294 14.9017 17.5433 14.9016 17.2521V14.9075C14.9017 14.6163 14.8249 14.3302 14.6789 14.0781C14.533 13.8259 14.323 13.6165 14.0702 13.4709L12.135 12.3555L17.6765 9.1651H17.689L17.716 9.14851L17.7409 9.12779C17.7495 9.12201 17.7572 9.11502 17.7638 9.10706C17.7712 9.10068 17.7782 9.09375 17.7846 9.08632L17.7971 9.07596V9.06352L17.8158 9.03864L17.8345 9.01169L17.849 8.98475L17.8615 8.95986L17.874 8.93084C17.8784 8.92081 17.8818 8.91041 17.8844 8.89975V8.87487V8.8417C17.8854 8.83136 17.8854 8.82095 17.8844 8.81061C17.8854 8.80096 17.8854 8.79123 17.8844 8.78158C17.8844 8.78158 17.8844 8.78159 17.8844 8.765C17.9041 6.9917 17.2179 5.28297 15.9762 4.01353ZM13.6274 17.8844L11.6944 18.9977V16.3586L13.9808 15.036V17.2646C13.9805 17.3908 13.9469 17.5147 13.8835 17.6238C13.82 17.733 13.7288 17.8236 13.6191 17.8865L13.6274 17.8844ZM8.45594 17.2625V15.0381L10.7424 16.3586V18.9997L8.8093 17.8865C8.7013 17.8224 8.61192 17.7313 8.54996 17.6222C8.48799 17.5132 8.45559 17.3899 8.45594 17.2646V17.2625ZM11.2225 15.5398L8.93609 14.2192L10.8712 13.106C10.9803 13.0422 11.1044 13.0086 11.2308 13.0086C11.3573 13.0086 11.4814 13.0422 11.5904 13.106L13.5235 14.2192L11.2225 15.5398ZM9.75713 8.88109C10.1933 8.59844 10.7023 8.448 11.2225 8.448C11.7427 8.448 12.2517 8.59844 12.6879 8.88109L11.2225 11.4123L9.75713 8.88109ZM11.395 3.04956C11.4819 3.10674 11.5597 3.17663 11.6258 3.25686C11.8163 3.50007 11.9703 3.76962 12.0831 4.05706C12.3404 4.70804 12.5224 5.38617 12.6256 6.0783C12.7214 6.67135 12.7831 7.26937 12.8106 7.86944C12.315 7.63061 11.7718 7.50656 11.2215 7.50656C10.6711 7.50656 10.1279 7.63061 9.63242 7.86944C9.6708 6.72518 9.86091 5.59103 10.1978 4.49655C10.3245 4.0689 10.5219 3.66533 10.7819 3.30247C10.8558 3.204 10.9458 3.11853 11.0479 3.04956C11.0992 3.01524 11.1588 2.9952 11.2204 2.99151C11.283 2.99545 11.3434 3.01623 11.395 3.05162V3.04956ZM15.388 7.50665C14.8219 7.50515 14.2635 7.63728 13.7584 7.89224C13.7172 6.63856 13.5075 5.39607 13.1348 4.19804C13.0216 3.84818 12.8762 3.50951 12.7004 3.18638C13.7801 3.47395 14.7532 4.0673 15.502 4.8947C16.2508 5.72209 16.7433 6.74811 16.9199 7.8487C16.4408 7.62355 15.9176 7.50742 15.388 7.50872V7.50665ZM9.73635 3.1843C9.64472 3.34868 9.56281 3.51828 9.49107 3.69221C9.20419 4.41081 9.00123 5.15998 8.88621 5.9249C8.77871 6.5761 8.70931 7.23299 8.67835 7.89224C8.18736 7.64625 7.64682 7.51453 7.09741 7.50699C6.548 7.49945 6.00404 7.61629 5.50644 7.8487C5.68395 6.74663 6.17797 5.71949 6.9287 4.89165C7.67942 4.06382 8.65466 3.47077 9.73635 3.1843ZM7.05498 8.44782C7.68337 8.44767 8.29183 8.6678 8.77396 9.06974L9.93173 11.0703L5.86188 8.72768C6.23257 8.54399 6.64105 8.44889 7.05498 8.44989V8.44782ZM13.6711 9.06974C14.0716 8.73933 14.5591 8.53112 15.0753 8.47009C15.5914 8.40905 16.1142 8.49777 16.5811 8.72561L12.5112 11.062L13.6711 9.06974Z"
      fill="#00007E"
    />
    <path d="M14.708 14.0762V14.079H14.7108L14.708 14.0762Z" fill="#00007E" />
    <path
      d="M10.3915 19.8815L10.3887 19.8787L10.3915 19.8815Z"
      fill="#00007E"
    />
    <path
      d="M12.0565 19.8787H12.0537V19.8815L12.0565 19.8787Z"
      fill="#00007E"
    />
    <path
      d="M28 12.4739C28 9.05336 29.9705 6.76469 32.9117 6.76469C34.7824 6.76469 36.1335 7.71416 36.8755 9.53846L36.9691 9.51565C36.7496 8.51431 36.6471 7.49101 36.6635 6.46617V3H40.1617V17.7893H36.7591C36.7475 16.9837 36.8101 16.1787 36.9462 15.3845L36.8527 15.3617C36.2478 17.1197 34.8136 18.1334 32.9221 18.1334C29.9455 18.1334 28 15.8945 28 12.4739ZM36.8298 12.4511C36.8298 10.9025 35.8092 9.88673 34.2108 9.88673C32.6124 9.88673 31.5918 10.8797 31.5918 12.4511C31.5918 14.0225 32.6124 15.0238 34.2108 15.0238C35.8092 15.0238 36.8298 13.9997 36.8298 12.4511Z"
      fill="#00007E"
    />
    <path
      d="M49.8497 6.83516V10.2557C49.5043 10.2224 49.1574 10.2072 48.8104 10.2101C47.0727 10.2101 46.0313 11.0642 46.0313 13.4441V17.7975H42.5269V7.11295H45.8401C45.8458 8.29123 45.7372 9.4673 45.5158 10.6247L45.6094 10.6496C46.1644 7.96705 47.3471 6.78955 49.0162 6.78955C49.2946 6.79004 49.5728 6.80527 49.8497 6.83516Z"
      fill="#00007E"
    />
    <path
      d="M62.2007 12.4511C62.2007 15.8716 59.6524 18.1375 56.1978 18.1375C52.7432 18.1375 50.1699 15.8716 50.1699 12.4511C50.1699 9.03051 52.7183 6.76465 56.1978 6.76465C59.6773 6.76465 62.2007 9.03051 62.2007 12.4511ZM53.6952 12.4511C53.6952 13.9768 54.7844 14.9388 56.1895 14.9388C57.5946 14.9388 58.6838 13.9686 58.6838 12.4511C58.6838 10.9336 57.5946 9.96339 56.1895 9.96339C54.7844 9.96339 53.6952 10.9253 53.6952 12.4511Z"
      fill="#00007E"
    />
    <path
      d="M76.1567 12.4739C76.1567 15.9421 74.1654 18.1375 71.245 18.1375C69.4595 18.1375 68.0003 17.2357 67.2957 15.3637L67.2042 15.3865C67.3342 16.0702 67.4037 16.7638 67.4121 17.4596V20.6666H63.9824V7.11292H67.4266C67.4413 7.92116 67.3561 8.72813 67.173 9.51561L67.2645 9.53841C67.9608 7.94422 69.1664 6.76465 71.2138 6.76465C74.0781 6.76465 76.1567 8.9621 76.1567 12.4739ZM67.3289 12.4511C67.3289 14.0225 68.3682 15.0175 69.9459 15.0175C71.5235 15.0175 72.5649 14.0453 72.5649 12.4511C72.5649 10.8569 71.4986 9.88669 69.9459 9.88669C68.3932 9.88669 67.3206 10.8776 67.3206 12.4511H67.3289Z"
      fill="#00007E"
    />
    <path
      d="M90.0831 12.4739C90.0831 15.9421 88.0897 18.1375 85.1693 18.1375C83.3859 18.1375 81.9246 17.2357 81.2304 15.3637L81.1368 15.3865C81.2667 16.0702 81.3363 16.7639 81.3447 17.4596V20.6666H77.9067V7.11292H81.353C81.367 7.92125 81.2812 8.72822 81.0974 9.51561L81.1909 9.53841C81.8851 7.94422 83.0907 6.76465 85.1298 6.76465C88.0045 6.76465 90.0831 8.9621 90.0831 12.4739ZM81.2532 12.4511C81.2532 14.0225 82.2925 15.0175 83.8723 15.0175C85.452 15.0175 86.4892 14.0453 86.4892 12.4511C86.4892 10.8569 85.425 9.88669 83.8723 9.88669C82.3196 9.88669 81.2449 10.8776 81.2449 12.4511H81.2532Z"
      fill="#00007E"
    />
  </svg>
);
